import React, { useEffect, useState } from 'react';
import { adminAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  UserPlus,
  Users,
  Loader2,
  Save,
  Shield,
  ShieldCheck,
  Eye,
  Trash2,
  KeyRound,
  Power,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const ROLE_META = {
  owner: { label: 'Owner', icon: ShieldCheck, classes: 'bg-amber-100 text-amber-800 border-amber-200' },
  admin: { label: 'Admin', icon: Shield, classes: 'bg-sky-100 text-sky-800 border-sky-200' },
  viewer: { label: 'Viewer', icon: Eye, classes: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function Admins() {
  const [me, setMe] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '' });
  const [inviting, setInviting] = useState(false);
  const [pwResetDialog, setPwResetDialog] = useState({ open: false, admin: null });
  const [newPassword, setNewPassword] = useState('');
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [meRes, listRes] = await Promise.all([
        authAPI.getMe(),
        adminAPI.list(),
      ]);
      setMe(meRes.data);
      setAdmins(listRes.data);
    } catch (e) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const isOwner = me?.role === 'owner';

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.password || !inviteForm.name) {
      toast.error('Name, email and password are required');
      return;
    }
    if (inviteForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setInviting(true);
    try {
      await adminAPI.create(inviteForm);
      toast.success(`${inviteForm.email} added as admin`);
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', password: '' });
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add admin');
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (admin, role) => {
    if (admin.role === role) return;
    try {
      await adminAPI.update(admin.id, { role });
      toast.success(`${admin.email} is now a ${ROLE_META[role]?.label || role}`);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const toggleActive = async (admin) => {
    try {
      await adminAPI.update(admin.id, { active: !admin.active });
      toast.success(admin.active ? 'Account deactivated' : 'Account activated');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await adminAPI.update(pwResetDialog.admin.id, { password: newPassword });
      toast.success(`Password reset for ${pwResetDialog.admin.email}`);
      setPwResetDialog({ open: false, admin: null });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin ${admin.email}? This cannot be undone.`)) return;
    try {
      await adminAPI.delete(admin.id);
      toast.success('Admin deleted');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    const t = toast.loading('Preparing database backup — this may take up to a minute...');
    try {
      const res = await adminAPI.downloadBackup();
      // Extract filename from Content-Disposition header if present
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `iida_db_backup_${Date.now()}.tar.gz`;

      const blob = new Blob([res.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${(blob.size / 1024).toFixed(1)} KB)`, { id: t });
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to prepare backup';
      toast.error(detail, { id: t });
    } finally {
      setDownloadingBackup(false);
    }
  };

  return (
    <Layout activeNav="admins">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
              <Users className="w-7 h-7" />
              Admins
            </h1>
            <p className="text-muted-foreground">
              Manage who can access the IIDA CPD Companion. {!isOwner && 'You need the Owner role to invite or modify other admins.'}
            </p>
          </div>
          {isOwner && (
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleDownloadBackup}
                variant="outline"
                className="gap-2"
                disabled={downloadingBackup}
                title="Download a fresh tar.gz snapshot of the entire database"
                data-testid="download-backup-btn"
              >
                {downloadingBackup ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {downloadingBackup ? 'Preparing…' : 'Download DB Backup'}
              </Button>
              <Button onClick={() => setInviteOpen(true)} className="gap-2" data-testid="invite-admin-btn">
                <UserPlus className="w-4 h-4" />
                Add Admin
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-list">
            {admins.map((a) => {
              const meta = ROLE_META[a.role] || ROLE_META.admin;
              const Icon = meta.icon;
              const isSelf = a.id === me?.id;
              return (
                <Card key={a.id} className="border-border" data-testid={`admin-row-${a.id}`}>
                  <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-foreground">{a.name}</h4>
                        {isSelf && (
                          <Badge variant="secondary" className="text-xs">You</Badge>
                        )}
                        {!a.active && (
                          <Badge variant="secondary" className="bg-rose-100 text-rose-700 text-xs">
                            Deactivated
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{a.email}</p>
                    </div>

                    {/* Role */}
                    {isOwner && !isSelf ? (
                      <Select value={a.role} onValueChange={(v) => changeRole(a, v)}>
                        <SelectTrigger className="w-[120px] h-9" data-testid={`role-select-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className={`gap-1 ${meta.classes}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </Badge>
                    )}

                    {/* Actions */}
                    {isOwner && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Reset password"
                          onClick={() => setPwResetDialog({ open: true, admin: a })}
                          data-testid={`reset-pw-${a.id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        {!isSelf && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              title={a.active ? 'Deactivate' : 'Activate'}
                              onClick={() => toggleActive(a)}
                              data-testid={`toggle-active-${a.id}`}
                            >
                              <Power className={`w-4 h-4 ${a.active ? '' : 'text-rose-600'}`} />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Delete admin"
                              onClick={() => handleDelete(a)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-admin-${a.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Invite dialog */}
        <Dialog open={inviteOpen} onOpenChange={(o) => !o && setInviteOpen(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a new admin</DialogTitle>
              <DialogDescription>
                Set a username and password yourself — no email is sent. The new admin can sign in immediately with what you set below. Default role is Admin.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite}>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="inv-name">Full name</Label>
                  <Input
                    id="inv-name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Dr Jane Smith"
                    data-testid="invite-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-email">Username</Label>
                  <Input
                    id="inv-email"
                    type="text"
                    autoComplete="off"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="e.g. jsmith  or  jane@clinic.com.au"
                    data-testid="invite-email-input"
                    required
                  />
                  <p className="text-xs text-muted-foreground">The new admin will sign in using this exact value. Email format is optional.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-password">Password</Label>
                  <Input
                    id="inv-password"
                    type="text"
                    value={inviteForm.password}
                    onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                    placeholder="Min 8 characters — share this securely"
                    data-testid="invite-password-input"
                    required
                  />
                  <p className="text-xs text-muted-foreground">No email is sent. Share these credentials with the new admin directly.</p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={inviting} className="gap-2" data-testid="confirm-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Add admin
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Password reset dialog */}
        <Dialog open={pwResetDialog.open} onOpenChange={(o) => !o && setPwResetDialog({ open: false, admin: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset password</DialogTitle>
              <DialogDescription>
                Set a new password for {pwResetDialog.admin?.email}. Share the new password securely.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="new-pw">New password (min 8 characters)</Label>
              <Input
                id="new-pw"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPwResetDialog({ open: false, admin: null })}>Cancel</Button>
              <Button onClick={resetPassword} data-testid="confirm-reset-pw-btn">Reset password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
